import React, { useEffect, useRef } from 'react';
import styled from 'astroturf/react';

import { NewsType } from './types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface Watcher {
  // Manually stop watching for impressions
  stop: () => void;
}

const intersecting = new Map<string, number>();
const viewed = new Set();

const watchViews = (
  element: HTMLElement,
  viewKey: string,
  threshold: number,
  sendView: () => Promise<void>,
): Watcher => {
  const hasViewed = (): boolean => viewed.has(viewKey);

  const handleIntersection = (
    entries: IntersectionObserverEntry[],
    observer: IntersectionObserver,
  ): void => {
    if (hasViewed()) {
      observer.disconnect();
      return;
    }

    const metThreshold = entries[0].intersectionRatio >= threshold;
    const filledRoot =
      entries[0].rootBounds != null &&
      // Target height is more than viewport height
      entries[0].boundingClientRect.height >= entries[0].rootBounds.height &&
      // We need to use an estimation of 90% here because this handler
      // is not executed on scroll but on meeting our pre-defined
      // 10% intersection threshold steps
      entries[0].intersectionRect.height >= 0.9 * entries[0].rootBounds.height;

    if (entries[0].isIntersecting && (metThreshold || filledRoot)) {
      if (!intersecting.has(viewKey)) {
        intersecting.set(
          viewKey,
          window.setTimeout(() => {
            sendView();
            viewed.add(viewKey);
            observer.disconnect();
          }, 5000),
        );
      }
    } else {
      clearTimeout(intersecting.get(viewKey));
      intersecting.delete(viewKey);
    }
  };

  // Threshold is set to every 10% to strike a balance between the
  // number of times the handler is executed and the accuracy of
  // detecting if a larger-than-viewport element has filled up the
  // entire screen.
  const observer = new IntersectionObserver(handleIntersection, {
    threshold: new Array(10).fill(1).map((v, i) => (v + i) / 10.0),
  });
  if (!hasViewed()) {
    observer.observe(element);
  }

  return {
    // Manually stop watching for impressions
    stop: () => observer.disconnect(),
  };
};

interface Props {
  // Allow external styling of the impression watcher container
  className?: string;
  type: NewsType | 'comment';
  id: number | string;
  sendView: () => Promise<void>;
  threshold: number;
  children?: React.ReactNode;
  keyPrefix?: string;
}

const ViewsWatcher: React.FunctionComponent<Props> = ({
  className,
  type,
  id,
  sendView,
  threshold,
  children,
  keyPrefix = '',
}) => {
  const container = useRef<HTMLDivElement | null>(null);
  const watcher = useRef<Watcher | null>(null);
  const viewKey = `${keyPrefix}:${type}:${id}`;

  // Set up impression watcher
  useEffect(() => {
    const cleanup = () => {
      watcher.current?.stop();
    };

    if (container.current == null) return cleanup;

    const impressionWatcher = watchViews(
      container.current,
      viewKey,
      threshold,
      sendView,
    );
    watcher.current = impressionWatcher;

    return cleanup;
  }, [container, watcher, sendView, threshold, viewKey]);

  return (
    <Container ref={container} className={className}>
      {children}
    </Container>
  );
};

export default ViewsWatcher;
